import { apiBase, fetchUsingApi } from "@/api/rest/core/apiClient";
import { ApiResponse } from "@/api/rest/core/types";
import {
  Case,
  CaseMessage,
  CaseMessageRaw,
  CaseMessageRequest,
  CaseRaw,
  CaseResponse,
  CaseType,
  NewCaseRequest,
  NewWarrantyCaseResponse,
  statusMap,
  SubmitCaseRequestWithoutFiles,
  UpdateCaseStatusRequest,
  WarrantySelfCheckRequest,
  WarrantySelfCheckResponse,
} from "@/api/rest/models/warranty";
import { ApiChatMessage } from "@/api/rest/services/chatService";
import { appendUploadsToFormData, UploadedFile } from "@/api/rest/services/uploadService";
import dayjs from "@/components/dayjsExtended";

const enrichWarrantyCaseResponse = (response: CaseResponse): CaseResponse => {
  return {
    case: enrichWarrantyCase([response.case])[0],
    messages: enrichWarrantyCaseMessages(response.messages),
  };
};

const enrichWarrantyCaseMessage = (m: CaseMessageRaw): CaseMessage => {
  return {
    ...m,
    createdAtTimestamp: dayjs(m.createdAt),
  };
};

const enrichWarrantyCaseMessages = (messages: CaseMessageRaw[]): CaseMessage[] => {
  return messages.map(enrichWarrantyCaseMessage);
};

const enrichWarrantyCase = (cases: CaseRaw[]): Case[] => {
  return cases.map((c) => ({
    ...c,
    createdAtTimestamp: dayjs(c.createdAt),
    localisedStatus: statusMap.get(c.type)?.get(c.status) ?? c.status.toString(),
  }));
};

export const getAllCases = async (householdId: number, type?: CaseType): Promise<ApiResponse<Case[]>> => {
  const typeParam = type ? `&type=${type}` : "";
  return await fetchUsingApi(
    `${apiBase}/warranty/v1/list?householdId=${householdId}${typeParam}`,
    "GET",
    enrichWarrantyCase,
  );
};

export const getAllCasesForBuilding = async (buildingId: number, type?: CaseType): Promise<ApiResponse<Case[]>> => {
  const typeParam = type ? `&type=${type}` : "";
  return await fetchUsingApi(
    `${apiBase}/warranty/v1/list/building?buildingId=${buildingId}${typeParam}`,
    "GET",
    enrichWarrantyCase,
  );
};

export const getCase = async (caseId: number): Promise<ApiResponse<CaseResponse>> => {
  return await fetchUsingApi(`${apiBase}/warranty/v1/${caseId}/get`, "GET", enrichWarrantyCaseResponse);
};

export const sendMessage = async (caseId: number, request: CaseMessageRequest): Promise<ApiResponse<CaseMessage>> => {
  return await fetchUsingApi(
    `${apiBase}/warranty/v1/${caseId}/message`,
    "POST",
    enrichWarrantyCaseMessage,
    JSON.stringify(request),
  );
};

export const sendFiles = async (
  caseId: number,
  accountId: number,
  uploads: UploadedFile[],
): Promise<ApiResponse<CaseMessage[]>> => {
  const formData = new FormData();
  formData.append("accountId", accountId.toString());
  console.log("[FileUpload] sendFiles: caseId=%d, uploads=%o", caseId, uploads);
  appendUploadsToFormData(formData, uploads);
  const result = await fetchUsingApi(
    `${apiBase}/warranty/v1/${caseId}/files`,
    "POST",
    enrichWarrantyCaseMessages,
    formData,
  );
  if (!result.success) {
    console.warn("[FileUpload] sendFiles failed: caseId=%d, result=%o", caseId, result);
  } else {
    console.log("[FileUpload] sendFiles succeeded: caseId=%d, messagesCount=%d", caseId, result.result?.length ?? 0);
  }
  return result;
};

export const fileNewCase = async (request: NewCaseRequest): Promise<ApiResponse<NewWarrantyCaseResponse>> => {
  return await fetchUsingApi(
    `${apiBase}/warranty/v1/new`,
    "POST",
    (e) => e as NewWarrantyCaseResponse,
    JSON.stringify(request),
  );
};

export const getWarrantySelfCheck = async (
  request: WarrantySelfCheckRequest,
): Promise<ApiResponse<WarrantySelfCheckResponse>> => {
  return await fetchUsingApi(
    `${apiBase}/warranty/v1/self-check`,
    "POST",
    (e) => e as WarrantySelfCheckResponse,
    JSON.stringify(request),
  );
};

export const WarrantyDraftFlow = {
  LEGACY: "LEGACY",
  STRUCTURED: "STRUCTURED",
} as const;

export type WarrantyDraftFlow = (typeof WarrantyDraftFlow)[keyof typeof WarrantyDraftFlow];

export type WarrantyDraftEntryRole = "USER" | "ASSISTANT" | "SYSTEM";
export type WarrantyDraftEntryType = "TEXT" | "FILE" | "IMAGE";

export interface WarrantyDraftEntry {
  id: number;
  role: WarrantyDraftEntryRole;
  type: WarrantyDraftEntryType;
  content: string;
  documentId?: number | null;
  filename?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
}

export interface WarrantyDraftFile {
  entryId: number;
  documentId?: number | null;
  filename?: string | null;
  type: WarrantyDraftEntryType;
  content: string;
}

export interface WarrantyDraft {
  draftId: number;
  status: "ACTIVE" | "SUBMITTED" | "DISCARDED";
  scopeKey: string;
  state?: Record<string, any> | null;
  entries: WarrantyDraftEntry[];
  files: WarrantyDraftFile[];
}

export const getLatestWarrantyDraft = async (
  householdId: number,
  flowType: WarrantyDraftFlow,
): Promise<ApiResponse<WarrantyDraft | null>> => {
  return await fetchUsingApi(
    `${apiBase}/warranty/v1/drafts/latest?householdId=${householdId}&flowType=${flowType}`,
    "GET",
    (e) => (e ? (e as WarrantyDraft) : null),
  );
};

export const uploadWarrantyDraftFiles = async (
  draftId: number,
  uploads: UploadedFile[],
): Promise<ApiResponse<WarrantyDraft>> => {
  const formData = new FormData();
  appendUploadsToFormData(formData, uploads);
  return await fetchUsingApi(
    `${apiBase}/warranty/v1/drafts/${draftId}/files`,
    "POST",
    (e) => e as WarrantyDraft,
    formData,
  );
};

export const deleteWarrantyDraft = async (draftId: number): Promise<ApiResponse<null>> => {
  return await fetchUsingApi(`${apiBase}/warranty/v1/drafts/${draftId}`, "DELETE", () => null);
};

export const forceSubmitCase = async (
  request: SubmitCaseRequestWithoutFiles,
  uploads: UploadedFile[],
  history: ApiChatMessage[],
): Promise<ApiResponse<CaseRaw>> => {
  const formData = new FormData();
  console.log("[FileUpload] forceSubmitCase: householdId=%d, uploads=%o", request.householdId, uploads);
  Object.entries(request).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      formData.append(key, String(value));
    }
  });
  appendUploadsToFormData(formData, uploads);
  formData.append("historyJson", JSON.stringify(history));
  const result = await fetchUsingApi(`${apiBase}/warranty/v1/submit`, "POST", (e) => e as CaseRaw, formData);
  if (!result.success) {
    console.warn("[FileUpload] forceSubmitCase failed: householdId=%d, result=%o", request.householdId, result);
  } else {
    console.log("[FileUpload] forceSubmitCase succeeded: caseId=%s", result.result?.id);
  }
  return result;
};

export const updateStatus = async (caseId: number, request: UpdateCaseStatusRequest): Promise<ApiResponse<CaseRaw>> => {
  return await fetchUsingApi(
    `${apiBase}/warranty/v1/${caseId}/status`,
    "PUT",
    (e) => e as CaseRaw,
    JSON.stringify(request),
  );
};

export const markReplyHandled = async (caseId: number): Promise<ApiResponse<CaseRaw>> => {
  return await fetchUsingApi(`${apiBase}/warranty/v1/${caseId}/closed-follow-up/close`, "POST", (e) => e as CaseRaw);
};

// Structured Warranty Types and API

export enum WarrantyPhase {
  TRIAGE = "TRIAGE",
  DETAIL_EXTRACTION = "DETAIL_EXTRACTION",
  COMPLETED = "COMPLETED",
}

export interface ChatMessageDto {
  role: string;
  content: string;
}

export interface StructuredWarrantyRequest {
  householdId: number;
  buildingId: number;
  history: ChatMessageDto[];
  phase: WarrantyPhase;
  selectedCategoryId?: string | null;
  accountId?: number | null;
  draftId?: number | null;
  flowType?: string | null;
}

export interface SubmitWarrantyArgs {
  subcontractorEmail: string;
  title: string;
  description: string;
  likelyUnderWarranty: boolean;
  // These fields are added by WarrantySubmissionForm when user selects a subcontractor
  subcontractorName?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
}

export interface StructuredWarrantyResponse {
  phase: WarrantyPhase;
  selectedCategoryId?: string | null;
  selectedCategoryName?: string | null;
  message?: string | null;
  submissionData?: SubmitWarrantyArgs | null;
  draftId?: number | null;
}

export interface StructuredWarrantyEnabledResponse {
  enabled: boolean;
}

export interface WarrantyCategory {
  id: string;
  name: string;
  description: string;
  keywords: string[];
  documentIds: number[];
  customPrompt?: string | null;
}

export interface WarrantySubcontractor {
  id: string;
  name: string;
  email: string;
  categories: WarrantyCategory[];
}

export interface DefaultWarrantyCategory {
  name?: string | null;
  description?: string | null;
  email: string;
  documentIds: number[];
  customPrompt?: string | null;
}

export interface StructuredWarrantyRules {
  enabled: boolean;
  subcontractors: WarrantySubcontractor[];
  defaultCategory?: DefaultWarrantyCategory | null;
}

export const processStructuredWarranty = async (
  request: StructuredWarrantyRequest,
): Promise<ApiResponse<StructuredWarrantyResponse>> => {
  return await fetchUsingApi(
    `${apiBase}/warranty/v1/warranty/structured`,
    "POST",
    (e) => e as StructuredWarrantyResponse,
    JSON.stringify(request),
  );
};

export const isStructuredWarrantyEnabled = async (
  buildingId: number,
): Promise<ApiResponse<StructuredWarrantyEnabledResponse>> => {
  return await fetchUsingApi(
    `${apiBase}/warranty/v1/warranty/structured/enabled/${buildingId}`,
    "GET",
    (e) => e as StructuredWarrantyEnabledResponse,
  );
};

export const getStructuredWarrantyRules = async (
  householdId: number,
): Promise<ApiResponse<StructuredWarrantyRules>> => {
  return await fetchUsingApi(
    `${apiBase}/warranty/v1/warranty/structured/rules?householdId=${householdId}`,
    "GET",
    (e) => e as StructuredWarrantyRules,
  );
};
